import {FC, SyntheticEvent, useEffect, useState} from 'react';
import {Box, Grid, Typography} from '@mui/material';
import dayjs from 'dayjs';
import {Form, Formik, FormikProps} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import {AMPInput} from 'src/components/common/AMPInput';
import {getHelperText} from 'src/utils/forms';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {ContractStatusManualEnum} from 'src/enums';
import {CancelButton} from 'src/components/common/CancelButton';
import {AMPDatePicker} from '../../common/AMPDatePicker';
import {ContractManualFormType, contractManualSchema} from '../formData/createManualContract';
import {AMPAutocomplete} from '../../common/AMPAutocomplete';
import {AutocompleteValue, BaseUserInfo, BusinessDTO} from '../../../types';
import {CreateContractGeneralFormProps} from './CreateContractGeneralForm.types';
import {actionsWrapperSx, formSx} from './CreateContractGeneralForm.sx';
import {findAutocompleteValueById, isErrorObject} from 'src/utils/helper';
import {TableFilterFormTypes} from 'src/components/reports/TableFilter/TableFilter.types';
import {AMPAutocompleteProps} from 'src/components/common/AMPAutocomplete/AMPAutocomplete.types';
import {businessesApi, usersApi} from '../../../api';
import {getBusinessesData} from '../../../pages/Contracts';
import {useBusinessId, useAppSelector} from '../../../app/hooks';
import {store} from 'src/app/store';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {NotifyError} from 'src/utils/notification';
import {UserTypeEnum} from 'src/enums/common';

type ClientsQueryTrigger = ReturnType<typeof usersApi.useLazyGetUsersQuery>[0];

export const getClientsData = async (
    trigger: ClientsQueryTrigger,
    authBusinessId: number | undefined,
    page: number,
    size: number,
    keyword?: string
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            data: {
                businessId: authBusinessId,
                contractExists: false,
                userTypes: [UserTypeEnum.PRIVATE_DRIVER, UserTypeEnum.COMPANY],
                keyword,
            },
            params: {
                size,
                page,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const CreateContractGeneralForm: FC<CreateContractGeneralFormProps> = ({
                                                                                  handleClose,
                                                                                  handleSubmit,
                                                                                  initialValues,
                                                                                  businessId,
                                                                              }) => {
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);
    // The one the caller named, or the business being worked in when it named none.
    const activeBusinessId = useBusinessId();
    const pageSizeBusiness = 10;
    const pageSizeClient = 1000; // Get all clients in one request

    const [pageBusiness] = useState(0);
    const [pageClients, setPageClients] = useState(0);
    const [businessData, setBusinessData] = useState<Array<BusinessDTO>>([]);
    const [clientsData, setClientsData] = useState<Array<BaseUserInfo>>([]);
    const [keyClient, setKeyClient] = useState('');

    const [getClients, clients] = usersApi.useLazyGetUsersQuery();
    const [getBusinesses, businesses] = businessesApi.useLazyGetBusinessesQuery();

    useEffect(() => {
        void getClientsData(getClients, businessId ?? activeBusinessId, pageClients, pageSizeClient);
    }, [businessId]);

    useEffect(() => {

    }, []);

    const processedClients = (): AutocompleteValue[] =>
        Object.values(
            clientsData.map((item) => {
                const id = item?.id ?? 0;
                const image = item?.avatar?.url ?? '';

                // Company user case
                if (!item?.firstName && !item?.lastName) {
                    return {
                        id,
                        image,
                        label: item?.company?.name ?? 'Unknown Company',
                    };
                }

                // Driver user case - format name properly
                const firstName = item?.firstName?.trim() || '';
                const lastName = item?.lastName?.trim() || '';
                const fullName = `${firstName} ${lastName}`.trim();

                return {
                    id,
                    image,
                    label: fullName || item?.emailAddress || 'Unknown User',
                };
            })
        );

    const processedContractStatus = () => {
        return Object.values(ContractStatusManualEnum).map((item, index) => ({
            id: index,
            name: intl.formatMessage({id: item}),
            value: item,
        }));
    };

    useEffect(() => {
        const content = businesses?.data?.content ?? [];
        const allData = [...businessData, ...content].reduce((acc, cur) => {
            return {
                ...acc,
                [cur.id]: cur,
            };
        }, {});
        setBusinessData(Array.from(Object.values(allData)));
    }, [businesses?.data?.content]);

    useEffect(() => {
        const content = clients?.data?.content ?? [];
        const allData = [...clientsData, ...content].reduce((acc, cur) => {
            return {
                ...acc,
                [cur.id]: cur,
            };
        }, {});
        setClientsData(Array.from(Object.values(allData)));
    }, [clients?.data?.content]);

    useEffect(() => {
        void getBusinessesData(getBusinesses, pageSizeBusiness, pageBusiness);
    }, [pageBusiness]);

    useEffect(() => {
        void getClientsData(getClients, businessId ?? activeBusinessId, pageClients, pageSizeClient, keyClient);
    }, [pageClients]);

    const handleChangeClientValue =
        (
            setFieldValue: FormikProps<TableFilterFormTypes>['setFieldValue'],
            fieldName: string
        ): AMPAutocompleteProps['onChange'] =>
            (_e, newValue) => {
                if (newValue) {
                    setFieldValue(fieldName, newValue.id);
                }
            };

    const handleInputClients = () => (_event: SyntheticEvent, value: string) => {
        setPageClients(0);
        setKeyClient(value);
        getClientsData(getClients, businessId ?? activeBusinessId, pageClients, pageSizeClient, value);
    };
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={contractManualSchema}
            onSubmit={handleSubmit}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<ContractManualFormType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="clientInfo"/>
                            </Typography>
                            <Grid
                                container
                                rowSpacing={2}
                                columnSpacing={{xs: 0, md: 2}}
                                sx={{
                                    flexDirection: 'row',
                                }}
                            >
                                <Grid
                                    xs={12}
                                    lg={6}
                                    item
                                >
                                    <AMPAutocomplete
                                        onInputChange={handleInputClients()}
                                        options={processedClients()}
                                        value={findAutocompleteValueById(processedClients(), +values['userId'])}
                                        label={'client'}
                                        input={'userId'}
                                        showAvatar
                                        onChange={handleChangeClientValue(setFieldValue, 'userId')}
                                        onBlur={handleBlur}
                                        description="selectDriverDescription"
                                        hasError={touched['userId'] && Boolean(errors['userId'])}
                                        helperText={getHelperText('userId', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="contractInfo"/>
                            </Typography>
                            <Grid
                                container
                                rowSpacing={2}
                                columnSpacing={{xs: 0, md: 2}}
                            >
                                <Grid
                                    xs={12}
                                    lg={6}
                                    item
                                >
                                    <AMPInput
                                        value={values['contractId']}
                                        type={'text'}
                                        label={'contractId'}
                                        input={'contractId'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['contractId'] && Boolean(errors['contractId'])}
                                        helperText={getHelperText('contractId', touched, errors, intl)}
                                    />
                                </Grid>
                                <Grid
                                    xs={12}
                                    lg={6}
                                    item
                                >
                                    <AMPFormSelect
                                        data={processedContractStatus() ?? []}
                                        value={values['contractStatus']}
                                        label={'contractStatus'}
                                        input={'contractStatus'}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                rowSpacing={2}
                                columnSpacing={{xs: 0, md: 2}}
                            >
                                <Grid
                                    xs={12}
                                    lg={6}
                                    item
                                >
                                    <AMPDatePicker
                                        label="contractCreationDate"
                                        value={dayjs(values['contractCreatedAt']).valueOf()}
                                        onChange={(val) =>
                                            setFieldValue(
                                                'contractCreatedAt',
                                                dayjs(dayjs(val).format('YYYY-MM-DD')).valueOf()
                                            )
                                        }
                                    />
                                </Grid>
                                {values['contractStatus'] === ContractStatusManualEnum.OPERATION && (
                                    <Grid
                                        xs={12}
                                        lg={6}
                                        item
                                    >
                                        <AMPDatePicker
                                            label="contractOperationDate"
                                            value={dayjs(values['operationalAt']).valueOf()}
                                            onChange={(val) =>
                                                setFieldValue(
                                                    'operationalAt',
                                                    dayjs(dayjs(val).format('YYYY-MM-DD')).valueOf()
                                                )
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                    <Box sx={actionsWrapperSx}>
                        <CancelButton
                            text="cancel"
                            handler={handleClose}
                        />
                        <SubmitButton
                            text="next"
                            color="primary"
                            isSubmitting={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
